import { Heart, MapPin, Users, Award } from "lucide-react";
import { useInView } from "@/hooks/useAnimations";

const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/0b4azit5_Andi-Werner-Foto-4565-Print.jpg";

export default function AboutUs() {
  const [ref, isInView] = useInView();

  return (
    <section
      ref={ref}
      id="ueber-uns"
      data-testid="about-section"
      className="py-20 lg:py-28 bg-white"
      aria-label="Über uns"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className={`relative transition-all duration-700 ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            {/* Decorative frame behind image */}
            <div className="absolute -inset-3 bg-gradient-to-br from-cyan-100/60 via-transparent to-green-100/60 rounded-[2rem] -z-10" />

            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/15 ring-1 ring-slate-200/50">
              <img
                src={ABOUT_IMG}
                alt="Dennis Lehnen und Karsten Weinacht – Gründer von Lehnen & Weinacht Pflegeberatung & Alltagsbegleitung NRW"
                className="w-full h-auto object-cover object-top aspect-[4/5]"
                data-testid="about-image"
                loading="lazy"
              />
            </div>

            {/* Badge overlay */}
            <div className="absolute -bottom-5 -right-3 lg:-bottom-6 lg:-right-6 bg-white rounded-2xl p-4 sm:p-5 shadow-xl border border-slate-100/80">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 bg-cyan-50 rounded-xl flex items-center justify-center">
                  <Heart className="h-5 w-5 text-cyan-700" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-slate-800 font-['Manrope']">500+</p>
                  <p className="text-xs sm:text-sm text-slate-500">Beratene Familien</p>
                </div>
              </div>
            </div>

            {/* Name labels */}
            <div className="absolute top-4 left-4 flex gap-2">
              <span className="bg-white/90 backdrop-blur-sm text-xs font-semibold text-slate-700 px-3 py-1.5 rounded-lg shadow-sm">
                Dennis Lehnen
              </span>
              <span className="bg-white/90 backdrop-blur-sm text-xs font-semibold text-slate-700 px-3 py-1.5 rounded-lg shadow-sm">
                Karsten Weinacht
              </span>
            </div>

            {/* Decorative blurs */}
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-cyan-100/40 rounded-full blur-2xl -z-10" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-green-100/40 rounded-full blur-2xl -z-10" />
          </div>

          {/* Content */}
          <div className={`transition-all duration-700 delay-200 ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <span className="inline-block text-sm font-semibold text-cyan-700 bg-cyan-50 px-4 py-1.5 rounded-full mb-4">
              Über uns
            </span>
            <h2 className="font-['Manrope'] text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              Persönlich. Digital. Aus NRW.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Lehnen & Weinacht steht für professionelle Pflegeberatung und Alltagsbegleitung 
              in Nordrhein-Westfalen. Wir verbinden langjährige Erfahrung in der Pflege mit den 
              Vorteilen moderner Technik.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Unser Ziel: Ihnen und Ihren Angehörigen den Zugang zur Pflegeberatung so einfach 
              wie möglich zu machen — ohne komplizierte Formulare, ohne lange Wartezeiten.
            </p>

            {/* Credentials */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl" data-testid="about-credential-0">
                <MapPin className="h-5 w-5 text-cyan-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Lokal in NRW</p>
                  <p className="text-sm text-slate-500">Spezialisiert auf regionale Angebote</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl" data-testid="about-credential-1">
                <Award className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Zertifizierte Berater</p>
                  <p className="text-sm text-slate-500">Nach § 7a SGB XI qualifiziert</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl" data-testid="about-credential-2">
                <Users className="h-5 w-5 text-cyan-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Erfahrenes Team</p>
                  <p className="text-sm text-slate-500">Jahrelange Erfahrung in der Pflege</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl" data-testid="about-credential-3">
                <Heart className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Mit Herz beraten</p>
                  <p className="text-sm text-slate-500">Empathisch und verständlich</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
